import { createRouter, createWebHistory } from "vue-router";
import Dashboard from "../views/Dashboard.vue";
import Analysis from "../views/analysis/Analysis.vue";
import Ads from "../views/Ads.vue";
import AdStats from "../views/AdStats.vue";
import Admins from "../views/Admins.vue";
import Verification from "../views/Verification.vue";
import Config from "../views/Config.vue";
import PaymentMethods from "../views/PaymentMethods.vue";
import Users from "../views/Users.vue";
import UserRelations from "../views/UserRelations.vue";
import Referral from "../views/Referral.vue";
import Messages from "../views/Messages.vue";
import UserDetail from "../views/UserDetail.vue";
import Complaints from "../views/Complaints.vue";
import ComplaintDetail from "../views/ComplaintDetail.vue";
import ComplianceRules from "../views/ComplianceRules.vue";
import MediaComplianceRules from "../views/MediaComplianceRules.vue";
import Support from "../views/Support.vue";
import EmailInbox from "../views/EmailInbox.vue";
import Signin from "../views/Signin.vue";
import store from "@/store";
import Signout from "../views/Signout.vue";
import { applyPageTitle } from "@/services/pageTitle";
import { ROLES } from "@/services/acl";

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/dashboard",
    meta: { requiresAuth: true },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/analysis",
    name: "Analysis",
    component: Analysis,
    meta: {
      requiresAuth: true,
      screenName: "Analysis",
      requiredRole: ROLES.ANALYSIS_INDEX,
    },
  },
  {
    path: "/ads",
    name: "Ads",
    component: Ads,
    meta: {
      requiresAuth: true,
      screenName: "Ads",
      requiredRole: ROLES.ADS_INDEX,
    },
  },
  {
    path: "/ads/:id/stats",
    name: "AdStats",
    component: AdStats,
    meta: {
      requiresAuth: true,
      screenName: "Ad statistics",
      requiredRole: ROLES.ADS_INDEX,
    },
  },
  {
    path: "/users",
    name: "Users",
    component: Users,
    meta: {
      requiresAuth: true,
      screenName: "Users",
      requiredRole: ROLES.USER_INDEX,
    },
  },
  {
    path: "/user-relations",
    name: "UserRelations",
    component: UserRelations,
    meta: {
      requiresAuth: true,
      screenName: "User relations",
      requiredRole: ROLES.USER_INDEX,
    },
  },
  {
    path: "/messages",
    name: "Messages",
    component: Messages,
    meta: {
      requiresAuth: true,
      screenName: "Messages",
      requiredRole: ROLES.CHAT_INDEX,
    },
  },
  {
    path: "/referrals",
    name: "Referral",
    component: Referral,
    meta: {
      requiresAuth: true,
      screenName: "Referral",
      requiredRole: ROLES.GROWTH_INDEX,
    },
  },
  {
    path: "/users/:id",
    name: "UserDetail",
    component: UserDetail,
    meta: {
      requiresAuth: true,
      screenName: "User profile",
      requiredRole: ROLES.USER_INDEX,
    },
  },
  {
    path: "/verification",
    name: "Verification",
    component: Verification,
    meta: {
      requiresAuth: true,
      screenName: "Verification",
      requiredRole: ROLES.VERIFICATION_INDEX,
    },
  },
  {
    path: "/complaints",
    name: "Complaints",
    component: Complaints,
    meta: {
      requiresAuth: true,
      screenName: "Complaints",
      requiredRole: ROLES.COMPLAINT_INDEX,
    },
  },
  {
    path: "/complaints/:id",
    name: "ComplaintDetail",
    component: ComplaintDetail,
    meta: {
      requiresAuth: true,
      screenName: "Complaints",
      requiredRole: ROLES.COMPLAINT_INDEX,
    },
  },
  {
    path: "/complaints/reported/:id",
    name: "ComplaintDetailReported",
    component: ComplaintDetail,
    meta: {
      requiresAuth: true,
      screenName: "Complaints",
      complaintGroup: "reportedUserId",
      requiredRole: ROLES.COMPLAINT_INDEX,
    },
  },
  {
    path: "/complaints/reporter/:id",
    name: "ComplaintDetailReporter",
    component: ComplaintDetail,
    meta: {
      requiresAuth: true,
      screenName: "Complaints",
      complaintGroup: "reporterUserId",
      requiredRole: ROLES.COMPLAINT_INDEX,
    },
  },
  {
    path: "/compliance-rules",
    name: "ComplianceRules",
    component: ComplianceRules,
    meta: {
      requiresAuth: true,
      screenName: "Compliance rules",
      requiredRole: ROLES.COMPLIANCE_RULE_INDEX,
    },
  },
  {
    path: "/media-compliance-rules",
    name: "MediaComplianceRules",
    component: MediaComplianceRules,
    meta: {
      requiresAuth: true,
      screenName: "Media compliance rules",
      requiredRole: ROLES.MEDIA_COMPLIANCE_RULE_INDEX,
    },
  },
  {
    path: "/support",
    name: "Support",
    component: Support,
    meta: {
      requiresAuth: true,
      screenName: "Support",
      requiredRole: ROLES.SUPPORT_INDEX,
    },
  },
  {
    path: "/email",
    name: "EmailInbox",
    component: EmailInbox,
    meta: {
      requiresAuth: true,
      screenName: "Email Inbox",
      requiredRole: ROLES.EMAIL_MANAGEMENT_INDEX,
    },
  },
  {
    path: "/admins",
    name: "Admins",
    component: Admins,
    meta: {
      requiresAuth: true,
      screenName: "Administrators",
      requiredRole: ROLES.ADMIN_INDEX,
    },
  },
  {
    path: "/payment-methods",
    name: "PaymentMethods",
    component: PaymentMethods,
    meta: {
      requiresAuth: true,
      screenName: "Payment methods",
      requiredRole: ROLES.PAYMENT_METHOD_INDEX,
    },
  },
  {
    path: "/config",
    name: "Config",
    component: Config,
    meta: {
      requiresAuth: true,
      screenName: "Configuration",
      requiredRole: ROLES.CONFIG_INDEX,
    },
  },
  {
    path: "/signin",
    name: "Signin",
    component: Signin,
    meta: { guestOnly: true },
  },
  {
    path: "/signout",
    name: "Signout",
    component: Signout,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = Boolean(to.meta?.requiresAuth);
  const guestOnly = Boolean(to.meta?.guestOnly);
  let hasJwt = store.getters.hasJwt;

  if (hasJwt && !store.state.authInitialized) {
    try {
      await store.dispatch("initializeAuth");
    } catch (error) {
      if (error?.status === 401) {
        hasJwt = false;
      } else {
        console.error("Failed to initialize session", error);
      }
    }
  }

  hasJwt = store.getters.hasJwt;

  if (requiresAuth && !hasJwt) {
    const query = to.fullPath ? { redirect: to.fullPath } : {};
    return next({ name: "Signin", query });
  }

  if (guestOnly && hasJwt) {
    return next({ name: "Dashboard" });
  }

  const requiredRole = to.meta?.requiredRole;
  if (requiredRole && !store.getters.hasRole(requiredRole)) {
    return next({ name: "Dashboard" });
  }

  return next();
});

router.afterEach((to) => {
  const screenNameFromMeta =
    typeof to.meta?.screenName === "string" && to.meta.screenName.trim()
      ? to.meta.screenName
      : null;
  const screenName =
    screenNameFromMeta || (typeof to.name === "string" ? to.name : undefined);

  applyPageTitle({
    appName: store.getters.tenantAppName,
    screenName,
  });
});

export default router;
